import { useToast } from "@app/components/toast";
import { getAuthToken } from "@app/relay/fetch-relay";
import { environment } from "@app/relay/relay-environment";
import { Link } from "@tanstack/react-router";
import { Archive, Download, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import {
  fetchQuery,
  graphql,
  useMutation,
  usePreloadedQuery,
} from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { backupsPage_Query } from "@app/queries/backupsPage_Query.graphql";
import type { backupsPage_ConfigBackupMutation } from "@app/queries/backupsPage_ConfigBackupMutation.graphql";

export const backupsQuery = graphql`
  query backupsPage_Query {
    configBackups {
      id
      filename
      byteSize
      stakeholderCount
      ruleCount
      takenBy
      createdAt
      downloadPath
    }
  }
`;

const configBackupMutation = graphql`
  mutation backupsPage_ConfigBackupMutation {
    configBackup {
      configBackup {
        id
      }
      errors {
        message
      }
    }
  }
`;

type Backup = backupsPage_Query["response"]["configBackups"][number];

const formatTimestamp = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const formatBytes = (bytes: number | null | undefined) => {
  if (bytes == null) return "—";
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const formatCount = (value: number | null | undefined) =>
  value == null ? "—" : value.toLocaleString();

export default function BackupsPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<backupsPage_Query>;
}) {
  const preloaded = usePreloadedQuery(backupsQuery, queryRef);
  const [data, setData] = useState(preloaded);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [commit, backingUp] =
    useMutation<backupsPage_ConfigBackupMutation>(configBackupMutation);
  const toast = useToast();

  // Re-sync the local mirror only when the route hands a NEW query, keyed on
  // queryRef — NOT on `preloaded`: refresh() writes the same query root into the
  // store, and keying on `preloaded` would re-emit the stale snapshot and clobber
  // what the refresh just fetched.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setData(preloaded), [queryRef]);

  const refresh = () =>
    fetchQuery<backupsPage_Query>(
      environment,
      backupsQuery,
      {},
      { fetchPolicy: "network-only" }
    )
      .toPromise()
      .then((fresh) => {
        if (fresh) setData(fresh);
      });

  const backups = data.configBackups;

  const takeBackup = () =>
    commit({
      variables: {},
      onCompleted: (response) => {
        const error = response.configBackup?.errors[0]?.message;
        if (error) {
          toast({ message: error, kind: "error" });
          return;
        }
        toast({ message: "Backup taken.", kind: "success" });
        refresh();
      },
      onError: (error) => toast({ message: error.message, kind: "error" }),
    });

  // Downloaded through the app rather than a bare link: the document lives in
  // object storage behind an account-scoped endpoint, and the
  // browser cannot attach the bearer token to a plain <a href>.
  const download = async (row: Backup) => {
    if (!row.downloadPath) return;

    setDownloading(row.id);
    try {
      const response = await fetch(row.downloadPath, {
        headers: { Authorization: `Bearer ${getAuthToken() ?? ""}` },
      });
      if (!response.ok) {
        toast({
          message:
            response.status === 404
              ? "That backup is no longer available — take a new one."
              : "Download failed — please try again.",
          kind: "error",
        });
        return;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = row.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ message: "Download failed — please try again.", kind: "error" });
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="p-6 max-w-full mx-auto space-y-6">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-xl font-bold">Config backups</h1>
        <Link
          to="/data-health"
          className="ml-auto px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm"
        >
          ← Back to data health
        </Link>
        <button
          type="button"
          onClick={() => refresh()}
          className="px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm inline-flex items-center gap-1"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
        <button
          type="button"
          disabled={backingUp}
          onClick={takeBackup}
          className="rounded bg-gray-900 text-white px-3 py-1.5 text-sm font-medium disabled:opacity-50"
        >
          {backingUp ? "Backing up…" : "Back up everything"}
        </button>
      </div>

      <p className="text-sm text-gray-500 max-w-3xl">
        Each backup is everything in{" "}
        <span className="font-medium">this account</span> that is not
        re-derivable from the raw statements: counterparties, rules, manual
        attributions and classifications, loans, dividend assignments, and every
        perspective with its categories. No other account&rsquo;s configuration
        is in it, and none of theirs is listed here. The file is stored in
        object storage, so it outlives the server it was taken on. Restore one
        with{" "}
        <code className="text-xs bg-gray-100 rounded px-1 py-0.5">
          rake config:import FILE=&lt;downloaded file&gt;
        </code>
        .
      </p>

      {backups.length === 0 ? (
        <div className="rounded border border-dashed border-gray-200 p-8 text-center text-gray-500">
          <Archive className="w-6 h-6 mx-auto mb-2 text-gray-300" />
          <div className="text-sm">No backups yet.</div>
          <div className="text-sm">
            Press <span className="font-medium">Back up everything</span> to
            take the first one.
          </div>
        </div>
      ) : (
        <div className="rounded border border-gray-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b">
                <th className="py-2 px-3 font-medium">Taken</th>
                <th className="py-2 px-3 font-medium">File</th>
                <th className="py-2 px-3 font-medium text-right">
                  Counterparties
                </th>
                <th className="py-2 px-3 font-medium text-right">Rules</th>
                <th className="py-2 px-3 font-medium text-right">Size</th>
                <th className="py-2 px-3 font-medium">By</th>
                <th className="py-2 px-3 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {backups.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="py-2 px-3 whitespace-nowrap">
                    {formatTimestamp(row.createdAt)}
                  </td>
                  <td className="py-2 px-3 font-mono text-xs text-gray-600">
                    {row.filename}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {formatCount(row.stakeholderCount)}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {formatCount(row.ruleCount)}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums">
                    {formatBytes(row.byteSize)}
                  </td>
                  <td className="py-2 px-3 text-gray-500">
                    {row.takenBy ?? "—"}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {row.downloadPath && (
                      <button
                        type="button"
                        disabled={downloading === row.id}
                        onClick={() => download(row)}
                        className="px-2 py-1 rounded border border-gray-200 text-gray-700 hover:bg-gray-50 text-xs inline-flex items-center gap-1 disabled:opacity-50"
                      >
                        <Download className="w-3.5 h-3.5" />
                        {downloading === row.id ? "Downloading…" : "Download"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
